import React from "react";

// Physical scale bar + magnification readout for the Viv DetailView.
//
// Viv's own ScaleBarLayer wants a physical-size-aware loader; we already have
// everything we need from the viewState, so we draw a plain HTML overlay. This
// also lets us show "N× native", which is what answers the reviewer's actual
// question ("how far zoomed am I?") — once you pass 1× you are magnifying L0
// pixels, so any remaining blockiness is empty magnification, not lost detail.
//
// Geometry: viewState.zoom z ⇒ 2^z screen-px per L0 image-px. Each L0 px is
// pxSizeUm µm, so screen-px per µm = 2^z / pxSizeUm.

function niceLength(rawUm: number): number {
  // Round DOWN to the nearest 1/2/5 × 10^n so the bar is a legible round value.
  const pow = Math.pow(10, Math.floor(Math.log10(rawUm)));
  const frac = rawUm / pow;
  const step = frac >= 5 ? 5 : frac >= 2 ? 2 : 1;
  return step * pow;
}

function fmtUm(um: number): string {
  if (um >= 1000) return `${(um / 1000).toFixed(um >= 10000 ? 0 : 1)} mm`;
  if (um >= 1)    return `${um % 1 === 0 ? um : um.toFixed(1)} µm`;
  return `${(um * 1000).toFixed(0)} nm`;
}

export function ScaleBar({
  zoom, pxSizeUm,
}: {zoom: number; pxSizeUm: number}) {
  const scale = Math.pow(2, zoom);            // screen px per L0 image px
  if (!Number.isFinite(scale) || scale <= 0) return null;
  const pxPerUm = scale / pxSizeUm;           // screen px per micron
  const targetPx = 130;                        // desired bar length on screen
  const rawUm = targetPx / pxPerUm;
  const um = niceLength(rawUm);
  const barPx = Math.round(um * pxPerUm);

  // Magnification relative to native (1 L0 px == 1 screen px). >1 ⇒ zoomed
  // past native (empty magnification); <1 ⇒ zoomed out below native.
  const mag = scale;
  const magStr = mag >= 1 ? `${mag.toFixed(mag >= 10 ? 0 : 1)}× native`
                          : `${(mag * 100).toFixed(0)}% native`;
  const overNative = mag > 1.05;

  return (
    <div style={{
      position: "absolute", left: 14, bottom: 14, zIndex: 8,
      pointerEvents: "none", userSelect: "none",
      background: "rgba(15,17,21,0.72)", border: "1px solid #2a3045",
      borderRadius: 6, padding: "6px 9px",
      font: "12px -apple-system, 'Segoe UI', sans-serif", color: "#e6e9ef",
    }}>
      <div style={{display: "flex", alignItems: "center", gap: 8}}>
        <div style={{
          width: barPx, height: 6, background: "transparent",
          borderLeft: "2px solid #e6e9ef", borderRight: "2px solid #e6e9ef",
          borderBottom: "2px solid #e6e9ef",
        }}/>
        <span style={{fontVariantNumeric: "tabular-nums", fontWeight: 600}}>
          {fmtUm(um)}
        </span>
      </div>
      <div style={{
        marginTop: 3, fontSize: 10.5,
        color: overNative ? "#f59e0b" : "#9ba2b3",
        fontVariantNumeric: "tabular-nums",
      }}>
        {magStr}{overNative ? " · past native res" : ""}
      </div>
    </div>
  );
}
